import type { Role, RoleProvenance } from '@ralph/contracts'
import type { SignInRefusalCause } from '@/components/people/people-plane.ts'
import { pluralCategory } from '@/i18n/format.ts'
import type { I18n } from '@/i18n/I18nProvider.tsx'

/**
 * `people` — every sentence the people plane shows (PRD-21).
 *
 * English is the source of truth AND the type (PRD-27 LOC-011): `Messages = typeof en`, so
 * the Portuguese sibling is checked against the shape of this file and a missing key is a
 * compile error rather than a runtime fallback.
 *
 * Anything that interpolates is a FUNCTION returning a whole sentence, never a template
 * assembled at the call site (LOC-013) — a translator handed 'Good ' and ', ' cannot
 * produce a language whose greeting puts the name first.
 *
 * Two rules of tone hold throughout:
 *
 * - **Say what will happen to a person, not what the system will do to a row.** "They will be
 *   signed out of 2 devices immediately" rather than "sessions will be revoked".
 * - **Never imply a control that cannot work.** A role that arrived from the deployment's
 *   configuration cannot be revoked here, and the copy says where it lives instead of offering
 *   a button that would 412.
 *
 * What is NOT here: the ladder itself, the refusal classifier and the three sentences that are
 * computed from them live in `components/people/people-plane.ts` (LOC-017, D-25). A catalogue
 * holds words; a rule that decides which words is code, and code that could drift per locale is
 * a bug rather than a translation.
 */

/**
 * What a sentence here needs from the reader's language (PRD-27 LOC-016, LOC-020).
 *
 * Passed in rather than imported. A catalogue is data, and one that reached for the locale
 * itself would have to capture it at import — which freezes the language on a switch with no
 * type error, no runtime error and no failing test (F-3). The caller has `useI18n()` inside its
 * component body; it hands the bag down.
 */
export type PeopleFmt = Pick<I18n, 'locale' | 'formatCount' | 'formatList'>

/**
 * Which of an entry's own plural forms to say.
 *
 * Never `count === 1`: Brazilian Portuguese puts ZERO in the `one` category ("0 pessoa"), so a
 * two-branch comparison is already wrong in one of the two shipped languages, on every empty
 * state, silently. The forms stay with the sentence that uses them rather than being passed as
 * an argument, so a translator sees the noun (F-12).
 */
const pick = (fmt: PeopleFmt, count: number, forms: { one: string; other: string }): string =>
  pluralCategory(fmt.locale, count) === 'one' ? forms.one : forms.other

/** The overwhelmingly common shape: the count, then its noun. */
const counted = (fmt: PeopleFmt, count: number, forms: { one: string; other: string }): string =>
  `${fmt.formatCount(count)} ${pick(fmt, count, forms)}`

/**
 * "Standard", never "Member", for the base role (PRD-26 D-37): "member" now means being in a
 * group, and one word for two things is how a person reading a profile concludes the wrong one.
 */
const ROLE_LABEL: Record<Role, string> = {
  user: 'Standard',
  auditor: 'Auditor',
  operator: 'Operator',
  admin: 'Administrator'
}

/**
 * The same four roles as they appear INSIDE a sentence — "the administrator role", "Give
 * auditor" — rather than as a label.
 *
 * Two written entries, never `roleLabel[role].toLowerCase()`. Lower-casing a label assumes an
 * orthography: German capitalises every noun, and a language whose role name is a proper noun
 * would be damaged by it. The `audienceSubject`/`audiencePhrase` pair in `marketplace.maker` is
 * the precedent (PRD-27 LOC-014).
 */
const ROLE_IN_SENTENCE: Record<Role, string> = {
  user: 'standard',
  auditor: 'auditor',
  operator: 'operator',
  admin: 'administrator'
}

/**
 * The roles as a group of people, for a filter that counts them.
 *
 * Written out, never the label with an English plural suffix bolted on — the line this PRD
 * quotes most: English morphology applied to a translated noun produces "Administradors" (F-10).
 */
const ROLE_PLURAL: Record<Role, string> = {
  user: 'Standards',
  auditor: 'Auditors',
  operator: 'Operators',
  admin: 'Administrators'
}

/** What each role opens, in the words of the surfaces it opens — used in the grant dialog. */
const ROLE_MEANING: Record<Role, string> = {
  user: 'Everybody who signs in has this. It cannot be granted or taken away.',
  auditor: 'Can read the audit trail — the record of who did what, across the platform.',
  operator: 'Can see platform activity, the model and tool configuration, and the audit trail.',
  admin: 'Can administer everything: agents, policies, knowledge, groups, and the access on this screen.'
}

export const people = {
  /**
   * All four role vocabularies are catalogue KEYS rather than module constants, even though
   * only two of them are read by name at a call site. A module-private map is invisible to
   * `Messages`, so a `pt-BR` file that left one in English would compile — and the plural map
   * is exactly the one that produces "Administradors" when it is not translated (F-10).
   */
  roleLabel: ROLE_LABEL,
  roleInSentence: ROLE_IN_SENTENCE,
  rolePlural: ROLE_PLURAL,
  roleMeaning: ROLE_MEANING,

  /**
   * "Operator also includes everything auditor can do."
   *
   * The ladder is `people-plane.ts`'s; the sentence is this file's. `beneath` arrives already
   * worded and already ordered, so the only thing left here is the grammar — which in
   * Portuguese means agreeing the verb with how many roles are named, something English does
   * not have to do and a shared sentence therefore cannot express.
   */
  rankDisclosure: (fmt: PeopleFmt, role: Role, beneath: readonly string[]): string =>
    `${ROLE_LABEL[role]} also includes everything ${fmt.formatList(beneath)} can do.`,

  title: 'People',
  lead: 'Who is here, what they may do, and where that came from.',

  deniedTitle: 'This view is for administrators and auditors',
  deniedBody:
    'It shows every account on the platform and the access each one has. Ask an administrator if you need it.',

  search: {
    label: 'Search people',
    placeholder: 'Name or email',
    empty: (query: string): string => `Nobody matches “${query}”.`,
    emptyBody: 'Try part of a name, or an email address.',
    clear: 'Clear the search'
  },

  filters: {
    all: 'Everyone',
    role: 'Role',
    status: 'Status',
    active: 'Active',
    /** The filter pill: a set of people, not one of them. Portuguese says these two differently. */
    deactivated: 'Deactivated',
    group: 'Group',
    anyGroup: 'In any group or none',
    counted: (fmt: PeopleFmt, role: Role, count: number): string => `${ROLE_PLURAL[role]} ${fmt.formatCount(count)}`
  },

  list: {
    loading: 'Loading the directory…',
    failed: 'The directory could not be loaded.',
    failedBody: 'GET /api/admin/users did not answer.',
    empty: 'Nobody here yet',
    emptyBody: 'Everyone who signs in appears here.',
    emptyGroup: 'Nobody is in this group',
    emptyGroupBody: 'People are added from the group’s page, or from a person’s profile here.',
    more: 'Show more',
    loadingMore: 'Loading…',
    you: 'you',
    /** The badge on one account, where the filter pill above is about a set of them (LOC-014). */
    deactivatedBadge: 'Deactivated',
    moreGroups: (fmt: PeopleFmt, count: number): string => `+${fmt.formatCount(count)}`,
    manages: 'Manages',
    moreGroupsLabel: (fmt: PeopleFmt, count: number): string =>
      `and ${counted(fmt, count, { one: 'more group', other: 'more groups' })}`,
    never: 'has never signed in',
    lastSeen: (when: string): string => `last signed in ${when}`,
    export: 'Export as CSV',
    total: (fmt: PeopleFmt, active: number, deactivated: number): string =>
      deactivated === 0
        ? counted(fmt, active, { one: 'person', other: 'people' })
        : `${fmt.formatCount(active)} active · ${fmt.formatCount(deactivated)} deactivated`
  },

  provenance: {
    floor: 'Everybody who signs in has this.',
    directory: 'From your directory, in the sign-in token.',
    configuration:
      'From this deployment’s sign-in configuration (AUTH_PROVIDERS_JSON). It can only be changed there, not here.',
    grant: (by: string | null, when: string): string =>
      by ? `Given by ${by} · ${when}` : `Given by the platform · ${when}`,
    grantPending: 'Waiting for a second administrator to approve it. It confers nothing until then.',
    overridden: 'Not in force: it is being removed here. See below.',
    /** `when` is `formatUntil` output, which already carries its own preposition in Portuguese. */
    expires: (when: string): string => `expires ${when}`,
    expired: 'expired',
    reason: (text: string): string => `“${text}”`,
    label: {
      floor: 'floor',
      directory: 'directory',
      configuration: 'configuration',
      grant: 'granted here'
    } satisfies Record<RoleProvenance, string>
  },

  person: {
    identity: 'Identity',
    identityLead: 'From the directory. Nothing here is editable on this platform.',
    provider: 'Signs in with',
    firstSeen: 'First seen',
    lastSeen: 'Last signed in',
    /**
     * The same two facts said mid-line, under the name: "first seen 3d ago · last signed in 5m
     * ago". Two written entries rather than `firstSeen.toLowerCase()`, for the reason
     * `roleInSentence` gives — a heading lower-cased to be reused is an assumption about an
     * orthography, and the reused word is often not the word a sentence wants anyway.
     */
    firstSeenLine: (when: string): string => `first seen ${when}`,
    lastSeenLine: (when: string): string => `last signed in ${when}`,
    subject: 'Directory id',
    mayDo: 'May do',
    mayDoLead: 'What this account can reach, and where each role came from.',
    grant: 'Give a role',
    remove: 'Remove',
    removeHere: 'Remove here',
    lift: 'Restore',
    approve: 'Approve',
    denied: 'Removed here',
    deniedStillSent: (role: Role): string => `Your directory still sends ${ROLE_IN_SENTENCE[role]}. This overrides it.`,
    deniedNoLongerSent: 'Your directory no longer sends this. Restoring it changes nothing until it does.',
    isIn: 'Groups',
    isInLead: 'What they get because of the groups they are in, and the groups they manage.',
    groupUnlocks: (fmt: PeopleFmt, bases: number, agents: number): string => {
      // A middle dot, not a conjunction: this is a readout of two tallies rather than a
      // sentence, so it stays a separator `Intl.ListFormat` has no opinion about.
      const parts: string[] = []
      if (bases > 0) parts.push(counted(fmt, bases, { one: 'knowledge base', other: 'knowledge bases' }))
      if (agents > 0) parts.push(counted(fmt, agents, { one: 'agent', other: 'agents' }))
      return parts.length === 0 ? 'Nothing is shared with it yet.' : `Gives ${parts.join(' · ')}`
    },
    groupMembers: (fmt: PeopleFmt, count: number): string => counted(fmt, count, { one: 'person', other: 'people' }),
    groupRelation: { member: 'member', manager: 'manager', both: 'member and manager' },
    /** A manager who is not in the group gets nothing it gives access to (PRD-26 D-3). */
    managesOnly: 'Manages who is in it and what it owns, without being in it.',
    noGroups: 'They are not in any group, and manage none.',
    addToGroup: 'Add to a group…',
    removeFromGroup: (group: string): string => `Remove from ${group}`,
    removeShort: 'Remove',
    stopManaging: 'Stop managing',
    stopManagingLabel: (group: string): string => `Stop managing ${group}`,
    addToGroupTitle: (name: string): string => `Add ${name} to a group`,
    addToGroupLead: 'They get what the group gives access to from their next request.',
    addToGroupSearch: 'Find a group',
    addToGroupPlaceholder: 'Group name',
    addToGroupNoMatch: (query: string): string => `No group matches “${query}”.`,
    addToGroupNone: 'There are no groups yet. An administrator creates them on the Groups page.',
    addToGroupFailed: 'Groups could not be loaded. Try again in a moment.',
    addToGroupMore: (fmt: PeopleFmt, shown: number, total: number): string =>
      `Showing ${fmt.formatCount(shown)} of ${fmt.formatCount(total)}. Type to narrow it down.`,
    addToGroupSubmit: 'Add',
    addToGroupWorking: 'Adding…',
    /** `why` is the groups plane's refusal phrase: "has been deactivated", "is a service account…". */
    addToGroupRefused: (name: string, why: string): string => `${name} ${why}, so was not added.`,
    alreadyIn: 'already in it',
    accountable: 'Accountable for',
    accountableLead: 'What becomes somebody else’s problem if this account is switched off.',
    ownsNothing: 'Nothing yet.',
    agents: (fmt: PeopleFmt, count: number): string => counted(fmt, count, { one: 'agent', other: 'agents' }),
    bases: (fmt: PeopleFmt, count: number): string =>
      counted(fmt, count, { one: 'knowledge base', other: 'knowledge bases' }),
    conversations: (fmt: PeopleFmt, count: number): string =>
      counted(fmt, count, { one: 'conversation', other: 'conversations' }),
    sessions: 'Signed in on',
    sessionsLead: 'Live sessions for this account.',
    noSessions: 'Not signed in anywhere right now.',
    thisDevice: 'this device',
    signOutEverywhere: 'Sign out everywhere',
    signedOut: (fmt: PeopleFmt, count: number): string =>
      `Ended ${counted(fmt, count, { one: 'session', other: 'sessions' })}.`,
    history: 'What was done to this account',
    noHistory: 'Nothing has been changed here yet.',
    deactivate: 'Deactivate account',
    reactivate: 'Reactivate account',
    deactivatedBy: (name: string | null, when: string): string =>
      name ? `Deactivated by ${name} · ${when}` : `Deactivated ${when}`,
    deactivatedReason: (reason: string): string => `Reason: ${reason}`,
    selfNote: 'This is you. An administrator cannot remove their own access or deactivate themselves.',
    pick: 'Select somebody to see what they may do.',
    pickBody: 'Every account on this platform is in the list.'
  },

  grantDialog: {
    title: (name: string, role: Role): string => `Give ${name} the ${ROLE_IN_SENTENCE[role]} role?`,
    denyTitle: (name: string, role: Role): string => `Remove ${name}’s ${ROLE_IN_SENTENCE[role]} access?`,
    denyLead:
      'Their directory sends this role, so it will come back at their next sign-in unless the platform overrides it. This override is that, and it takes effect immediately.',
    roleLabel: 'Role',
    meaning: (role: Role): string => ROLE_MEANING[role],
    until: 'For how long',
    untilForever: 'Until I remove it',
    untilDate: 'Until a date',
    dateLabel: 'Last day',
    reasonLabel: 'Why',
    reasonPlaceholder: 'covering Ana’s leave',
    reasonHint: 'Recorded with your name and shown on their profile.',
    reasonRequired: 'A reason is required for this role.',
    confirm: (role: Role): string => `Give ${ROLE_IN_SENTENCE[role]}`,
    confirmDeny: 'Remove it',
    cancel: 'Cancel',
    /** The dialog's only remaining control once a grant is waiting for a second administrator. */
    close: 'Close',
    working: 'Saving…',
    pendingApproval:
      'Recorded. A second administrator has to approve an administrator grant on this deployment, so it confers nothing yet.'
  },

  removeDialog: {
    title: (name: string, role: Role): string => `Take ${ROLE_IN_SENTENCE[role]} away from ${name}?`,
    lead: 'It stops applying on their next request. The record of the grant stays.',
    reasonLabel: 'Why',
    confirm: 'Remove it',
    working: 'Removing…'
  },

  deactivateDialog: {
    title: (name: string): string => `Deactivate ${name}?`,
    loading: 'Working out what this will cost…',
    sessions: (fmt: PeopleFmt, count: number): string =>
      count === 0
        ? 'They are not signed in anywhere right now.'
        : `They will be signed out of ${counted(fmt, count, { one: 'device', other: 'devices' })} immediately, and will not be able to sign in.`,
    // The verb is inside the plural form, not appended after it: Portuguese agrees the verb
    // with the count, and a sentence assembled as noun + verb has nowhere to put that.
    agents: (fmt: PeopleFmt, count: number): string =>
      `${counted(fmt, count, { one: 'agent they own moves', other: 'agents they own move' })} to the needs-an-owner queue, and keep working.`,
    bases: (fmt: PeopleFmt, count: number): string =>
      `${counted(fmt, count, { one: 'knowledge base they own moves', other: 'knowledge bases they own move' })} to the needs-an-owner queue, and keep answering.`,
    unmanagedGroups: (fmt: PeopleFmt, names: string[]): string =>
      pick(fmt, names.length, {
        one: `${names[0]} is left with no manager: nobody but an administrator can add or remove its members until someone is appointed.`,
        other: `${fmt.formatList(names)} are left with no manager: nobody but an administrator can change who is in them until someone is appointed.`
      }),
    conversations: (fmt: PeopleFmt, count: number): string =>
      `${counted(fmt, count, { one: 'conversation stays', other: 'conversations stay' })} theirs, and stay private.`,
    grants: (fmt: PeopleFmt, count: number): string =>
      `${counted(fmt, count, { one: 'role they were given is', other: 'roles they were given are' })} kept, inert, and come back if you reactivate them.`,
    nothingDeleted: 'Nothing is deleted. You can reactivate them at any time.',
    lastAdmin: 'This is the only administrator who can sign in. Give somebody else the administrator role first.',
    reasonLabel: 'Why',
    reasonPlaceholder: 'left the company',
    confirmNameLabel: (name: string): string => `Type ${name} to confirm`,
    confirmNamePlaceholder: 'Their name, exactly as it appears above',
    confirm: 'Deactivate',
    working: 'Deactivating…',
    cancel: 'Cancel',
    done: (name: string): string => `${name} is deactivated.`
  },

  reactivateDialog: {
    title: (name: string): string => `Reactivate ${name}?`,
    lead: 'They will be able to sign in again. Their old sessions stay ended, and the roles they were given come back.',
    reasonLabel: 'Why',
    confirm: 'Reactivate',
    working: 'Reactivating…'
  },

  access: {
    title: 'Access',
    pendingTab: 'Waiting',
    failuresTab: 'Could not sign in',
    settingsTab: 'Settings',

    pending: {
      title: 'Prepared for people who have not signed in',
      lead: 'A role can be ready before somebody’s first sign-in. No account is created until they arrive.',
      empty: 'Nothing waiting',
      emptyBody: 'Prepare a role for a new colleague and it applies the moment they first sign in.',
      add: 'Prepare a role',
      emailLabel: 'Email address',
      /** The domain is the deployment's and never translates (D-17); the local part is prose. */
      emailPlaceholder: 'colleague@evtit.com',
      roleLabel: 'Role',
      reasonLabel: 'Why',
      submit: 'Prepare it',
      applied: (email: string): string => `${email} already has an account, so the role is in effect now.`,
      appliedWaiting: (email: string): string =>
        `${email} already has an account. A second administrator has to approve it before it takes effect.`,
      prepared: (email: string): string => `Ready for ${email}. It applies at their first sign-in.`,
      preparedWaiting: (email: string): string =>
        `Ready for ${email}. A second administrator has to approve it before it takes effect.`,
      withdraw: 'Withdraw',
      claimBy: (when: string): string => `expires ${when} if unclaimed`,
      needsApproval: 'Needs a second administrator',
      needsApprovalHint:
        'This deployment requires a second administrator to approve an administrator grant. It will be waiting on their profile when they first sign in.',
      unclaimable: 'No sign-in provider on this deployment accepts that domain, so this can never be claimed.'
    },

    failures: {
      title: 'Sign-ins that were refused',
      lead: 'A refused sign-in leaves no account, so this is the only place it is visible.',
      empty: 'Nobody has been refused',
      emptyBody: 'A refusal appears here with its reason and how many times it happened.',
      attempts: (fmt: PeopleFmt, count: number): string => counted(fmt, count, { one: 'attempt', other: 'attempts' }),
      reactivate: 'Reactivate the account',
      reason: {
        deactivated: 'This account was deactivated here.',
        provider_conflict: 'This email is registered through a different identity provider.',
        unknown: 'The provider refused the sign-in.'
      } satisfies Record<SignInRefusalCause, string>
    },

    settings: {
      title: 'How access is administered',
      lead: 'Two knobs, both with consequences worth reading before you turn them.',
      fourEyes: 'Require a second administrator to approve an administrator grant',
      fourEyesHint:
        'Off by default, deliberately: with two administrators, turning this on means one resignation leaves nobody who can administer the platform.',
      ttl: 'A prepared role expires if unclaimed after',
      ttlUnit: 'days',
      defaultExpiry: 'Default expiry on new grants',
      defaultExpiryNone: 'No default — grants last until removed',
      defaultExpiryUnit: 'days',
      save: 'Save',
      saved: 'Saved.',
      working: 'Saving…'
    }
  },

  /**
   * The audit vocabulary, in words.
   *
   * The API answers *which* action; the interface owns the sentence (AGM-124). An administrator
   * reading their own history should not have to know that `access.deny.lifted` is what happens
   * when somebody presses Restore.
   *
   * Every line has an implied subject — the person whose panel this is — and no visible one, so
   * each is written to need no agreement with it. Portuguese would otherwise have to guess a
   * gender it is never told (PRD-27 D-21).
   */
  history: {
    'access.role.granted': 'was given a role',
    'access.role.denied': 'had a role removed here',
    'access.role.revoked': 'had a granted role taken back',
    'access.deny.lifted': 'had a removed role restored',
    'access.grant.approved': 'had a grant approved by a second administrator',
    'access.grant.prepared': 'had a role prepared before signing in',
    'account.deactivated': 'was deactivated',
    'account.reactivated': 'was reactivated',
    'account.sessions_revoked': 'was signed out everywhere',
    'access.admin_floor.held':
      'kept administrator access — the platform would otherwise have had nobody who could administer it'
  },

  /** The four events that name the group they were about; the name outlives a deleted group. */
  historyGroup: {
    added: (group: string): string => `was added to ${group}`,
    removed: (group: string): string => `was taken out of ${group}`,
    appointed: (group: string): string => `was made a manager of ${group}`,
    stoppedManaging: (group: string): string => `stopped managing ${group}`,
    /** When the audit row carried no name — a group deleted before the row was written. */
    unnamed: 'a group'
  },

  /** An action the catalogue does not know yet, and the role that no longer exists (D-37). */
  historyRetired: 'lost a role that no longer exists',
  historyUnknown: 'had a change recorded',
  retiredRole: 'a retired role',

  errors: {
    lastAdmin: 'That would leave the platform with no administrator.',
    selfDemotion: 'You cannot remove your own administrator access. Ask another administrator.',
    conflict: 'They already have that.',
    notFound: 'That is no longer there.',
    generic: 'That change was not saved.'
  },

  /** The person picker's sentences (`PersonPicker`, PRD-26 D-8). */
  picker: {
    searchLabel: 'Find a person',
    searchPlaceholder: 'Search by name or email',
    minChars: 'Type at least two letters of a name or an email address.',
    noMatch: (q: string): string => `Nobody here matches “${q}”. People appear once they have signed in.`,
    results: (fmt: PeopleFmt, count: number): string => counted(fmt, count, { one: 'person', other: 'people' }),
    failed: 'People could not be searched. Try again in a moment.',
    pasteToggle: 'Paste a list of email addresses',
    searchToggle: 'Search instead',
    pasteLabel: 'Email addresses, separated by commas, spaces or new lines',
    pastePlaceholder: 'ana@example.com, rui@example.com',
    resolve: 'Find these people',
    resolving: 'Looking them up…',
    found: (fmt: PeopleFmt, count: number): string =>
      `Found ${counted(fmt, count, { one: 'person', other: 'people' })}.`,
    // The resolve answer does not say why: nobody signed in yet, or a deactivated account. True for both.
    // The addresses stay a comma list rather than going through `formatList`: they are the input
    // the person pasted, read back as a set, not a phrase this language has to join.
    notFound: (emails: string[]): string =>
      `No active account for ${emails.join(', ')} — people can be added once they have signed in, and not while their account is deactivated.`,
    chosen: 'Chosen people',
    remove: (name: string): string => `Remove ${name}`
  }
}
